import { Typography } from '@remarkable/ark-web';
import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { useSendCommandSoftwareAutoInstall } from 'src/api/queries/enterprisePolicies';
import { Button, Modal, NotificationBox } from 'src/components';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { DeviceName } from 'src/components/DeviceName';

type DeviceActionSoftwareAutoInstallProps = {
  device: FleetDevice;
  userEmail: string | undefined;
  userId: string;
  onSuccess?: () => void;
};

export function DeviceActionSoftwareAutoInstall({
  device,
  userEmail,
  userId,
  onSuccess,
}: DeviceActionSoftwareAutoInstallProps) {
  const sendCommandSoftwareAutoInstall = useSendCommandSoftwareAutoInstall();

  return (
    <Modal.Content
      title="Enable software auto-install"
      className="flex flex-col gap-24"
    >
      <div>
        <Typography variant="body-lg" fontWeight="semibold" as="div">
          <DeviceIcon
            device={device}
            size={24}
            className="mr-6 inline align-sub"
          />
          <b>
            <DeviceName device={device} />
          </b>{' '}
          / <span className="whitespace-nowrap">{device.serialNumber}</span>
        </Typography>

        <Typography variant="body-lg" className="mt-8" as="div">
          (paired with {userEmail})
        </Typography>
      </div>

      <Modal.Description asChild>
        <Typography variant="body-md">
          This will automatically download the latest software on the device
          when it becomes available. The device must be connected to the
          internet for the update to be installed.
        </Typography>
      </Modal.Description>

      <div className="flex justify-center gap-12">
        <Modal.Close asChild>
          <Button variant="secondary">Close</Button>
        </Modal.Close>

        <Button
          loading={
            sendCommandSoftwareAutoInstall.isPending &&
            sendCommandSoftwareAutoInstall.variables?.enable === false
          }
          onClick={() => {
            sendCommandSoftwareAutoInstall.mutate(
              { deviceId: device.serialNumber, userId, enable: false },
              { onSuccess }
            );
          }}
        >
          Disable
        </Button>

        <Button
          variant="primary"
          loading={
            sendCommandSoftwareAutoInstall.isPending &&
            sendCommandSoftwareAutoInstall.variables?.enable === true
          }
          onClick={() => {
            sendCommandSoftwareAutoInstall.mutate(
              { deviceId: device.serialNumber, userId, enable: true },
              { onSuccess }
            );
          }}
        >
          Enable
        </Button>
      </div>

      <NotificationBox error={sendCommandSoftwareAutoInstall.error} />
    </Modal.Content>
  );
}
